import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { MenuAdminCategory } from '../api/types';
import { Alert, Spinner, money } from '../components/common';

/**
 * Sold-out control for the counter.
 *
 * One tap per item, applied immediately — this gets used mid-shift with a queue
 * waiting, so there is no save button to forget. Prices and names are not
 * editable here on purpose: running out of bananas is a decision made in a
 * hurry, and repricing the menu is not.
 */
export function StaffMenuPage() {
  const [menu, setMenu] = useState<MenuAdminCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMenu(await api.staffMenu());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(kind: 'product' | 'variant' | 'addon', id: string, available: boolean) {
    setBusy(id);
    setError(null);

    try {
      if (kind === 'product') await api.staffSetProductAvailability(id, available);
      else if (kind === 'variant') await api.staffSetVariantAvailability(id, available);
      else await api.staffSetAddOnAvailability(id, available);

      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function restoreAll() {
    if (!window.confirm('Put everything back on the menu?')) return;

    setBusy('all');
    setError(null);

    try {
      const { restored } = await api.staffRestoreMenu();
      await load();
      if (restored === 0) setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (!menu) return <Spinner label="Loading the menu…" />;

  const soldOut =
    menu.reduce(
      (n, c) =>
        n +
        c.products.filter((p) => !p.isAvailable).length +
        c.products.reduce((m, p) => m + p.variants.filter((v) => !v.isAvailable).length, 0) +
        c.addOns.filter((a) => !a.isAvailable).length,
      0,
    );

  return (
    <section className="page">
      <h1>Menu</h1>
      <p className="muted">
        Marking something sold out applies immediately. To change names, prices or pictures, use{' '}
        <Link to="/staff/menu/edit">Edit the menu</Link>.
      </p>
      <p className="muted small">
        Tap to mark something sold out. It disappears from the menu straight away and cannot be
        ordered — anyone with it already in their basket is told at checkout.
      </p>

      {error && <Alert>{error}</Alert>}

      <div className="menuadmin__bar">
        <span className={soldOut > 0 ? 'pill pill--bad' : 'pill pill--good'}>
          {soldOut === 0 ? 'Everything on the menu' : `${soldOut} sold out`}
        </span>
        {soldOut > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={busy !== null}
            onClick={restoreAll}
          >
            Put everything back
          </button>
        )}
      </div>

      {menu.map((category) => (
        <div key={category.id} className="panel">
          <h2 className="panel__title">{category.name}</h2>

          {category.products.map((product) => (
            <div key={product.id} className={product.isAvailable ? 'menurow' : 'menurow is-out'}>
              <div className="menurow__head">
                <button
                  type="button"
                  className={product.isAvailable ? 'btn btn--ghost btn--sm' : 'btn btn--primary btn--sm'}
                  disabled={busy !== null}
                  onClick={() => toggle('product', product.id, !product.isAvailable)}
                >
                  {product.isAvailable ? 'Mark sold out' : 'Back on'}
                </button>
                <strong>{product.name}</strong>
                {!product.isAvailable && <span className="pill pill--bad">SOLD OUT</span>}
              </div>

              {/* Sizes only matter while the drink itself is on. */}
              {product.isAvailable && product.variants.length > 0 && (
                <div className="menurow__sizes">
                  {product.variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={v.isAvailable ? 'sizebtn' : 'sizebtn is-out'}
                      disabled={busy !== null}
                      onClick={() => toggle('variant', v.id, !v.isAvailable)}
                      title={v.isAvailable ? 'Tap to mark this size sold out' : 'Tap to put this size back'}
                    >
                      {v.label} · {money(v.price)}
                      {!v.isAvailable && ' ✕'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {category.addOns.length > 0 && (
            <>
              <h3 className="section-heading">Add-ons</h3>
              <div className="menurow__sizes">
                {category.addOns.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={a.isAvailable ? 'sizebtn' : 'sizebtn is-out'}
                    disabled={busy !== null}
                    onClick={() => toggle('addon', a.id, !a.isAvailable)}
                  >
                    {a.name} · {money(a.price)}
                    {!a.isAvailable && ' ✕'}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ))}
    </section>
  );
}
